import { Draggable } from 'react-beautiful-dnd';
import { useCallback, useState, useEffect } from 'react';
import { TabComponent } from './TabComponent';
import { TabContents } from './TabContents';
import styled from 'styled-components';
import { useDeleteSingleView } from '../../deleteView/hooks/useDeleteSingleView';
import { getAiViewDataKey, getStoredAiViewData } from '../../utils/aiViewStorage';
import { Popover } from 'foundations-components/transitional/components/Popover';
import { Text } from 'foundations-components/components/Text';
import { Button } from 'foundations-components/transitional/components/Button';
import { ButtonWrapper } from 'foundations-components/transitional/components/ButtonWrapper';
import FormattedMessage from 'I18n/components/FormattedMessage';
import Raven from 'raven-js';
import { doSuccessAlert, doFailureAlert } from '../../deleteView/components/DeleteSingleViewModal';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const ENTER_KEY_KEYCODE = 13;
const StyledTabComponent = styled(TabComponent).withConfig({
  displayName: "ViewTab__StyledTabComponent",
  componentId: "sc-1jiken1-0"
})(["background-color:", ";"], ({
  theme,
  showAiViewColor
}) => showAiViewColor && theme.color['fill-accent-magenta-subtle']);
const useAiViewIds = (viewId, objectTypeId) => {
  const [isAiGenerated, setIsAiGenerated] = useState(false);
  useEffect(() => {
    if (!objectTypeId) return;
    try {
      const views = getStoredAiViewData(objectTypeId);
      setIsAiGenerated(Boolean(views === null || views === void 0 ? void 0 : views[viewId]));
    } catch (error) {
      console.error('Error reading AI view data from localStorage:', error);
      setIsAiGenerated(false);
    }
  }, [viewId, objectTypeId]);
  const removeFromAiViews = useCallback(() => {
    if (!objectTypeId) return;
    try {
      const views = getStoredAiViewData(objectTypeId);
      if (views !== null && views !== void 0 && views[viewId]) {
        delete views[viewId];
        if (Object.keys(views).length > 0) {
          localStorage.setItem(getAiViewDataKey(objectTypeId), JSON.stringify(views));
        } else {
          localStorage.removeItem(getAiViewDataKey(objectTypeId));
        }
        setIsAiGenerated(false);
      }
    } catch (error) {
      console.error('Error removing AI view data from localStorage:', error);
    }
  }, [viewId, objectTypeId]);
  return {
    isAiGenerated,
    removeFromAiViews
  };
};
export const ViewTab = ({
  icon,
  scrollToRef,
  isDragDisabled,
  tabId,
  index,
  children,
  active,
  onCloseClick,
  onTabClick,
  closeDisabled,
  closeTooltip,
  objectTypeId,
  currentlyEditingAiView,
  onRegenerateAiViewFunction,
  pinnedViewsBarLogger
}) => {
  const {
    isAiGenerated,
    removeFromAiViews
  } = useAiViewIds(tabId, objectTypeId);
  const [isDeletePopoverOpen, setIsDeletePopoverOpen] = useState(false);
  //Ai view color is only shown if the view is first time generated
  const showAiViewColor = (currentlyEditingAiView === null || currentlyEditingAiView === void 0 ? void 0 : currentlyEditingAiView.id) === tabId && !(currentlyEditingAiView !== null && currentlyEditingAiView !== void 0 && currentlyEditingAiView.isRegenerate);
  const [doDelete] = useDeleteSingleView({
    viewId: tabId,
    objectTypeId
  });
  const onTabClickFunction = useCallback(() => {
    onTabClick(tabId);
  }, [onTabClick, tabId]);
  const onCloseClickFunction = useCallback(() => onCloseClick(tabId), [onCloseClick, tabId]);
  const onDeleteAiViewFunction = useCallback(() => {
    setIsDeletePopoverOpen(true);
  }, []);
  const handleConfirmDelete = useCallback(() => {
    pinnedViewsBarLogger === null || pinnedViewsBarLogger === void 0 || pinnedViewsBarLogger.log({
      key: 'trackAiViewDelete',
      metadata: {
        objectTypeId,
        viewId: tabId
      }
    });
    onCloseClick(tabId);
    doDelete().then(() => {
      removeFromAiViews();
      setIsDeletePopoverOpen(false);
      doSuccessAlert('View');
    }).catch(error => {
      console.error('Error deleting view:', error);
      doFailureAlert();
      Raven.captureException(new Error('Error deleting new AI view'), {
        extra: {
          objectTypeId,
          viewId: tabId
        }
      });
      setIsDeletePopoverOpen(false);
    });
  }, [doDelete, removeFromAiViews, onCloseClick, tabId, objectTypeId, pinnedViewsBarLogger]);
  const onKeyDownFunction = useCallback(e => {
    if (e.which === ENTER_KEY_KEYCODE) {
      onTabClickFunction();
    }
  }, [onTabClickFunction]);
  return /*#__PURE__*/_jsx(Draggable, {
    //this and draggableId are very important for react-beautiful-dnd - they must be unique
    draggableId: tabId,
    index: index //index must be the index of this tab within the array, (it cannot be something arbitrary like key)
    ,
    isDragDisabled: isDragDisabled,
    children: (provided, snapshot) => /*#__PURE__*/_jsx(Popover, {
      open: isDeletePopoverOpen,
      onOpenChange: e => setIsDeletePopoverOpen(e.currentTarget.value),
      placement: "bottom",
      maxInlineSize: "400px",
      content: {
        header: /*#__PURE__*/_jsx(Text, {
          as: "h4",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "viewsManagement.modals.deleteViewPopover.title"
          })
        }),
        body: /*#__PURE__*/_jsx(Text, {
          typography: "body-100",
          marginBlockEnd: "100",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "viewsManagement.modals.deleteViewPopover.description"
          })
        }),
        footer: /*#__PURE__*/_jsxs(ButtonWrapper, {
          children: [/*#__PURE__*/_jsx(Button, {
            use: "danger",
            onClick: handleConfirmDelete,
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "viewsManagement.modals.deleteViewPopover.deleteButton"
            })
          }), /*#__PURE__*/_jsx(Button, {
            use: "tertiary-light",
            onClick: () => setIsDeletePopoverOpen(false),
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "viewsManagement.modals.deleteViewPopover.cancelButton"
            })
          })]
        })
      },
      children: /*#__PURE__*/_jsx(StyledTabComponent, Object.assign({
        role: "tab",
        "aria-selected": active,
        active: active,
        onClick: onTabClickFunction,
        onKeyDown: onKeyDownFunction,
        ref: provided.innerRef,
        "data-tab-id": tabId,
        "data-test-id": `view-tab-${tabId}`,
        canDropInCurrentLocation: Boolean(snapshot.draggingOver),
        showAiViewColor: showAiViewColor
      }, provided.draggableProps, provided.dragHandleProps, {
        children: /*#__PURE__*/_jsx(TabContents, {
          active: active,
          scrollToRef: scrollToRef,
          closeDisabled: closeDisabled,
          closeTooltip: closeTooltip,
          onCloseClickFunction: onCloseClickFunction,
          isDragDisabled: isDragDisabled,
          tabId: tabId,
          icon: icon,
          isAiGenerated: isAiGenerated,
          onDeleteViewFunction: onDeleteAiViewFunction,
          onRegenerateViewFunction: onRegenerateAiViewFunction,
          currentlyEditingAiView: currentlyEditingAiView,
          children: children
        })
      }))
    })
  }, tabId);
};